import React, { useState } from 'react';
import { Button, Input, message, Space, Table, Tooltip, Typography } from 'antd';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import PropTypes from 'prop-types';

import { CloseCircleFilled, EditOutlined, StarFilled } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from '@tt/dates/dayjs';

import { useManagementStore, useUserState } from '../../store';
import axios from '../../utilities/axios';

const { Title } = Typography;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const PctIdCell = ({ userId, record, updateFundedAccount, company }) => {
    const [editing, setEditing] = useState(false);
    const [inputValue, setInputValue] = useState('');

    // Helper function to get accounts by type
    const getAccountsByType = (user, type) => {
        if (!Array.isArray(user.fundedAccounts)) return [];
        return user.fundedAccounts
            .filter((acc) => acc.type === type)
            .map((acc) => acc.accountUniqueId);
    };

    // Helper function to get first funded account object by type (if multiple, use first)
    const getFirstFundedAccountByType = (user, type) => {
        if (!Array.isArray(user.fundedAccounts)) return null;
        return user.fundedAccounts.find((acc) => acc.type === type) || null;
    };

    const handleSave = () => {
        setEditing(false);
        const accountType = company === 'Peak Capital Trading' ? 'pct' : 'ptt';

        // Find the first funded account to update (if multiple, use first)
        const fundedAccount = getFirstFundedAccountByType(record, accountType);

        if (fundedAccount && fundedAccount.id) {
            updateFundedAccount(fundedAccount.id, {
                accountUniqueId: inputValue
            });
        } else {
            message.error('Funded account not found. Please contact support.');
        }
    };

    // For Trading Terminal, show only TTF IDs
    const renderTradingTerminalIds = () => {
        const ttfIds = getAccountsByType(record, 'simulator_ttf');

        return (
            <div>
                <span>Funded: {ttfIds.length > 0 ? ttfIds.join(', ') : '-'}</span>
            </div>
        );
    };

    // Get the appropriate ID based on company
    const getUserId = () => {
        if (company === 'Peak Capital Trading') {
            const pctAccounts = getAccountsByType(record, 'pct');
            return pctAccounts.length > 0 ? pctAccounts[0] : '';
        }
        if (company === 'Bear Bull Traders') {
            const pttAccounts = getAccountsByType(record, 'ptt');
            return pttAccounts.length > 0 ? pttAccounts[0] : '';
        }
        return userId || '';
    };

    const displayUserId = getUserId();

    let content;
    if (company === 'Trading Terminal') {
        // Trading Terminal - display only, no editing
        content = <>{renderTradingTerminalIds()}</>;
    } else if (editing) {
        content = (
            <>
                <Input
                    defaultValue={displayUserId}
                    maxLength={15}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                />
                <Button type="primary" onClick={handleSave}>
                    Save
                </Button>
                <Tooltip title="Cancel" key={record.id}>
                    <Button
                        type="text"
                        shape="circle"
                        onClick={() => setEditing(false)}
                        icon={<CloseCircleFilled />}
                    />
                </Tooltip>
            </>
        );
    } else if (displayUserId) {
        content = (
            <>
                <span>{displayUserId}</span>
                <Tooltip title="Edit" key={record.id}>
                    <Button
                        type="text"
                        shape="circle"
                        onClick={() => setEditing(true)}
                        icon={<span className="tt-edit"></span>}
                    />
                </Tooltip>
            </>
        );
    } else {
        content = (
            <>
                <span>-</span>
                <Tooltip title="Edit" key={record.id}>
                    <Button
                        type="text"
                        shape="circle"
                        onClick={() => setEditing(true)}
                        icon={<EditOutlined />}
                    />
                </Tooltip>
            </>
        );
    }

    return <Space style={{ width: 250, justifyContent: 'end' }}>{content}</Space>;
};

export default function UserManagementPage() {
    const [allUsersList, setAllUsersList] = useState([]);
    const { getUser } = useUserState();
    const user = getUser();
    const { company, setAllUsers } = useManagementStore((state) => state);

    // const navigate = useNavigate();

    // useEffect(() => {

    //   if (company === 'Personal') {

    //     navigate('/settings/1');

    //   }

    // }, [company, navigate]);
    const fetchUsers = () =>
        axios.get('crud/users', { headers: { token: user?.token } }).then(({ data }) => data);

    const { refetch } = useQuery(
        ['users', company],
        () =>
            fetchUsers().then(async (users) => {
                if (users.length > 0) {
                    // Helper function to check if user has accounts of a specific type
                    const hasAccountType = (userInput, type) => {
                        if (!Array.isArray(userInput.fundedAccounts)) return false;
                        return userInput.fundedAccounts.some((acc) => acc.type === type);
                    };

                    // Helper function to get accounts by type
                    const getAccountsByType = (userInput, type) => {
                        if (!Array.isArray(userInput.fundedAccounts)) return [];
                        return userInput.fundedAccounts
                            .filter((acc) => acc.type === type)
                            .map((acc) => acc.accountUniqueId);
                    };

                    const usersFiltered = users.filter((u) => {
                        if (company === 'Bear Bull Traders') {
                            return hasAccountType(u, 'ptt');
                        }

                        if (company === 'Peak Capital Trading') {
                            return hasAccountType(u, 'pct');
                        }

                        if (company === 'Trading Terminal') {
                            return hasAccountType(u, 'simulator_ttf');
                        }

                        return false;
                    });

                    const sortedUsers = usersFiltered.sort((a, b) => {
                        // Get all account IDs for each user
                        const aPctIds = getAccountsByType(a, 'pct');
                        const aPttIds = getAccountsByType(a, 'ptt');
                        const aTtfIds = getAccountsByType(a, 'simulator_ttf');

                        const bPctIds = getAccountsByType(b, 'pct');
                        const bPttIds = getAccountsByType(b, 'ptt');
                        const bTtfIds = getAccountsByType(b, 'simulator_ttf');

                        // Check if any ID contains the pattern (TRPCT, TRPTT, TTF)
                        const aIncludes =
                            aPctIds.some((id) => id?.includes('TRPCT')) ||
                            aPttIds.some((id) => id?.includes('TRPTT')) ||
                            aTtfIds.some((id) => id?.includes('TTF'));
                        const bIncludes =
                            bPctIds.some((id) => id?.includes('TRPCT')) ||
                            bPttIds.some((id) => id?.includes('TRPTT')) ||
                            bTtfIds.some((id) => id?.includes('TTF'));

                        if (aIncludes && !bIncludes) return -1;
                        if (!aIncludes && bIncludes) return 1;

                        // Sort alphabetically by the first available ID
                        const aId = aPctIds[0] || aPttIds[0] || aTtfIds[0] || '';
                        const bId = bPctIds[0] || bPttIds[0] || bTtfIds[0] || '';
                        return aId.localeCompare(bId);
                    });

                    setAllUsersList(sortedUsers);

                    const userSelectionFormat =
                        users
                            ?.map((u) => {
                                if (u.role !== 'admin') {
                                    // Get the first pct_id from fundedAccounts, or fall back to name
                                    const pctAccounts = getAccountsByType(u, 'pct');
                                    const displayLabel =
                                        pctAccounts.length > 0 && pctAccounts[0] !== ''
                                            ? pctAccounts[0]
                                            : u.name;

                                    return {
                                        value: u.id,
                                        label: displayLabel,
                                        token: u.token
                                    };
                                }

                                return null;
                            })
                            .filter(Boolean) || [];

                    setAllUsers(userSelectionFormat);
                } else {
                    setAllUsersList([]);
                }

                return users;
            }),
        { enabled: user.token !== undefined && user.role === 'admin' }
    );

    const updateFundedAccount = (fundedAccountId, accountData) =>
        axios
            .put(
                `crud/funded-accounts/${fundedAccountId}`,
                {
                    accountData
                },
                { headers: { token: user?.token } }
            )
            .then((response) => {
                if (response.status === 200) {
                    message.success('User ID updated successfully!');
                    refetch();
                }
            })
            .catch(() => {
                message.error('Something went wrong! Please report to the development team.');
            });

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email'
        },
        {
            title: 'User ID',
            key: 'user_id',
            render: (item, record) => {
                // For Trading Terminal, don't rely on dataIndex since it might not exist
                if (company === 'Trading Terminal') {
                    return (
                        <PctIdCell
                            userId={null}
                            record={record}
                            updateFundedAccount={updateFundedAccount}
                            company={company}
                        />
                    );
                }
                return (
                    <PctIdCell
                        userId={item}
                        record={record}
                        updateFundedAccount={updateFundedAccount}
                        company={company}
                    />
                );
            }
        },
        {
            title: 'Role',
            dataIndex: 'role',
            key: 'role',
            render: (item) => {
                if (!item) return '';

                if (item === 'admin') {
                    return (
                        <span>
                            {item.toUpperCase()} <StarFilled />
                        </span>
                    );
                }

                return <span>{item.charAt(0).toUpperCase() + item.slice(1)}</span>;
            }
        }
    ];

    return (
        <Space direction="vertical" align="start" style={{ width: '100%' }}>
            <Title level={4}>{company || 'List of'} Users</Title>

            {allUsersList && (
                <Table
                    columns={columns}
                    dataSource={allUsersList}
                    pagination={false}
                    rowKey="key"
                    style={{ width: 900 }}
                />
            )}
        </Space>
    );
}

PctIdCell.propTypes = {
    userId: PropTypes.string.isRequired,
    record: PropTypes.object.isRequired,
    updateFundedAccount: PropTypes.func.isRequired,
    company: PropTypes.string.isRequired
};
